import React, {
  useCallback, useState, useRef, useEffect,
} from 'react';
import propTypes from 'prop-types';
import classNames from 'classnames';

import { useAppStore } from '../../stores';

import { FloorPlanDefaultProps, FloorPlanPropTypes } from '../../utils/DrupalEntityTypes/FloorPlan/FloorPlanPropTypes';

import Modal from '../Modal/Modal';
import FloorPlanDetail from '../FloorPlan/FloorPlanDetail';
import FloorPlanInventoryList from '../FloorPlan/FloorPlanInventoryList';
import FavoriteButton from '../Personalization/Favorite/FavoriteButton';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';

import './FloorPlansPageListItem.scss';

const PRIORITY_LOAD_COUNT = 6; // Load first 6 images immediately

const FloorPlansPageListItem = ({
  data,
  index,
  showSoldOutStatus,
  showAvailableUnits,
  isInventoryOpen,
  onToggleInventory,
}) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalNavBtns, setModalNavBtnsState] = useState(null);
  const [modalHeaderData, setModalHeaderDataState] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imgHolderRef = useRef(null);
  const placeholderRef = useRef(null);

  // Stable callback references
  const setModalNavBtns = useCallback((navBtns) => {
    setModalNavBtnsState(navBtns);
  }, []);

  const setModalHeaderData = useCallback((headerData) => {
    setModalHeaderDataState(headerData);
  }, []);

  // Use shared IntersectionObserver, skip for first 6 items (priority loading)
  const isPriority = index < PRIORITY_LOAD_COUNT;
  const [containerRef, isVisible] = useIntersectionObserver(isPriority);

  // Force visibility check when index changes (e.g., after filtering)
  // This handles items that move into viewport due to other items being filtered out
  const [forceVisible, setForceVisible] = useState(false);
  useEffect(() => {
    if (!isPriority && !isVisible && !forceVisible && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const inViewport = (
        rect.top < window.innerHeight
        && rect.bottom > 0
        && rect.left < window.innerWidth
        && rect.right > 0
      );
      if (inViewport) {
        setForceVisible(true);
      }
    }
  }, [index, isPriority, isVisible, forceVisible, containerRef]);

  const shouldLoadImage = isVisible || forceVisible;

  const personalizationActive = useAppStore((state) => state.personalizationActive);

  const {
    uuid,
    title,
    coverImage,
    bedBathSqft,
    inventoryPhrase,
    availableResidences,
    floorPlanCategory,
  } = data;

  // Floor plan category labels are stored plural (e.g. "Cottages"), so use as-is
  // for the count phrase and strip a trailing "s" when there is only one.
  const availableCount = availableResidences?.length || 0;
  const availableTypeLabel = floorPlanCategory || 'Residences';
  const availableTypeSingular = availableTypeLabel.replace(/s$/i, '');

  // Load image when item becomes visible or is priority
  useEffect(() => {
    if (!shouldLoadImage || !coverImage || imageLoaded) {
      return;
    }

    const img = new Image();

    img.onload = () => {
      setImageLoaded(true);
      if (imgHolderRef.current && placeholderRef.current) {
        placeholderRef.current.classList.add('imgHolder__placeholder--hidden');
        imgHolderRef.current.classList.add('imgHolder--loaded');
      }
    };

    img.onerror = () => {
      setImageLoaded(true);
      if (imgHolderRef.current && placeholderRef.current) {
        placeholderRef.current.classList.add('imgHolder__placeholder--hidden');
        imgHolderRef.current.classList.add('imgHolder--loaded');
      }
    };

    img.src = coverImage;
  }, [shouldLoadImage, coverImage, imageLoaded]);

  const openModal = useCallback(() => {
    setModalIsOpen(true);
  }, []);

  // ALWAYS render all items initially - don't wait for level of care filter
  // This ensures fast initial render and all images can start lazy loading
  // Items will be filtered out by parent component (FloorPlansPageList) based on resultFloorPlans
  return (
    <>
      <Modal
        modalClassName="modal--anchored"
        isOpen={modalIsOpen}
        handleModalHide={() => setModalIsOpen(false)}
        showBlur
        showCloseButton
        title={title}
        headerData={modalHeaderData}
        navBtns={modalNavBtns}
      >
        <FloorPlanDetail
          uuid={uuid}
          data={showSoldOutStatus ? data : {
            ...data, inventoryPhrase: null, remainingInventory: null, soldOut: null,
          }}
          parent="modal"
          existingInventoryPhrase={showSoldOutStatus ? inventoryPhrase : null}
          setModalNavBtns={setModalNavBtns}
          setModalHeaderData={setModalHeaderData}
        />
      </Modal>

      <div className="gridItem" ref={containerRef}>
        <button
          className="modalOpen gridItemTrigger"
          onClick={openModal}
          type="button"
        >
          <div className="imgContainer">
            {coverImage ? (
              <>
                <div ref={placeholderRef} className="imgHolder__placeholder" />
                <div
                  ref={imgHolderRef}
                  className="imgHolder"
                  style={{ backgroundImage: `url(${coverImage})` }}
                />
              </>
            ) : (
              <div className="imgHolder imgHolder--noImage">
                No Floor Plan Image Available
              </div>
            )}

            { showSoldOutStatus && inventoryPhrase && (
              <div className="gridItemBanner">
                <span className="gridItemBanner__text">{ inventoryPhrase }</span>
              </div>
            )}

          </div>
        </button>

        { personalizationActive ? (
          <FavoriteButton
            type="floor_plan"
            uuid={uuid}
            hasShadow={false}
            className="favoriteBtn-positioner"
          />
        ) : null}

        <div className="floorplanInfo">
          <h2>{title}</h2>

          { bedBathSqft?.length > 0 && (
            <h3 className="floorplanInfo__subtitle">
              { bedBathSqft.map((item, index) => (
                <span
                  // eslint-disable-next-line react/no-array-index-key
                  key={`bedBathSqft-${index}`}
                  className="floorplanInfo__subtitle__group"
                >
                  {item}
                </span>
              ))}
            </h3>
          )}

          { showAvailableUnits && availableCount > 0 && (
            <button
              type="button"
              className="floorplanInfo__availableCount"
              onClick={() => onToggleInventory(uuid)}
              aria-expanded={isInventoryOpen}
              aria-controls={`available-residences-inventory:${uuid}`}
              data-testid={`available-residences-toggle:${uuid}`}
            >
              {availableCount}
              {' '}
              {availableCount === 1 ? availableTypeSingular : availableTypeLabel}
              {' '}
              Available
              <span
                className={classNames('floorplanInfo__availableCount__caret', {
                  'floorplanInfo__availableCount__caret--open': isInventoryOpen,
                })}
              />
            </button>
          )}

        </div>

        { showAvailableUnits && (
          <FloorPlanInventoryList
            uuid={uuid}
            availableResidences={availableResidences}
            floorPlanCategory={floorPlanCategory}
            isOpen={isInventoryOpen}
          />
        )}

      </div>
    </>
  );
};

FloorPlansPageListItem.propTypes = {
  data: FloorPlanPropTypes.data,
  index: propTypes.number.isRequired,
  showSoldOutStatus: propTypes.bool,
  showAvailableUnits: propTypes.bool,
  isInventoryOpen: propTypes.bool,
  onToggleInventory: propTypes.func,
};

FloorPlansPageListItem.defaultProps = {
  data: FloorPlanDefaultProps.data,
  showSoldOutStatus: false,
  showAvailableUnits: false,
  isInventoryOpen: false,
  onToggleInventory: () => {},
};

// Wrap with React.memo to prevent unnecessary re-renders
export default React.memo(FloorPlansPageListItem);
